import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { format } from 'date-fns'

import LgyLink from 'app/components/util/lgy-link/LgyLink'
import LgyTooltip from 'app/components/util/lgy-tooltip/LgyTooltip'
import LgyLoadingIcon from 'app/components/util/lgy-loading-icon/LgyLoadingIcon'
import AppraisalSearchBar from '../components/AppraisalSearchBar'
import { fetchAppraiser } from '../actions/fetchAppraiserAction'
import { fetchPendingAssignments, fetchUnacceptedAssignments } from '../actions/fetchAssignmentListsActions'
import { inactiveAppraiser } from './AppraisalContainer'
import { FaExclamationTriangle } from 'react-icons/fa'
import '../style/appraisal-mini-gear.scss'

const AppraisalMiniGear = ({ fetchAppraiser, fetchPendingAssignments, fetchUnacceptedAssignments, user, appraiserProfile, 
  pendingAssignments, unacceptedAssignments, fetching }) => {
    
  useEffect(() => {
    if (user.entities.some(e => e.name === 'AppraiserInspector')) {
      fetchAppraiser(user.entities.find(e => e.name === 'AppraiserInspector').values.id)
      fetchPendingAssignments()
      fetchUnacceptedAssignments()
    }
  }, [fetchAppraiser, fetchPendingAssignments, fetchUnacceptedAssignments, user])

  const renderPendingAssignments = (pendingAssignments, unacceptedAssignments) => {
    let fullList = unacceptedAssignments.list.concat(pendingAssignments.list)
    return <div className='mini-gear-pending-assignments'>
      <table className='usa-table-borderless'>
        <caption>Pending &amp; Suspended Assignments</caption>
        <thead>
          <tr>
            <th scope='col'>LIN</th>
            <th scope='col'>Assignment Date</th>
            <th scope='col'>Upload Indicator</th>
          </tr>
        </thead>
        <tbody>
          {(pendingAssignments.error || unacceptedAssignments.error) && <tr><td colSpan='3'>Error: Unable to load assignments.</td></tr>}
          {fullList && (fullList.length < 1 ?
            <tr><td colSpan='3'>
              You have no pending or suspended assignments.
              <LgyTooltip message='Click the arrow icon to view the full Appraiser Workspace' />
            </td></tr>
            : fullList.slice(0, 5).map(assignment =>
              <tr key={assignment.lin}>
                <th scope='row'>
                {(assignment.appraiserAccepted || assignment.appraiserAccepted === null) ? 
                  <LgyLink to={`/lgyhub/appraiser/${assignment.lin}`}>{assignment.lin}</LgyLink> 
                  : <LgyLink to='/lgyhub/appraiser'>{assignment.lin}</LgyLink>}
                  &nbsp;
                  {assignment.dispositionCode === 'S' &&
                    <span className='suspended-icon' data-info='LIN is in suspended status.' focusable='true' tabIndex='0'>
                      <FaExclamationTriangle color='orangered' />
                    </span>}
                </th>
                <td>{format(assignment.assignmentDate, 'MM/dd/yyyy', { awareOfUnicodeTokens: true })}</td>
                <td>
                  {assignment.salesContractUploadIndicator === 'Y' ? 'Available' : ''}
                  {assignment.salesContractUploadIndicator === 'N' ? 'Required' : ''}
                  {assignment.salesContractUploadIndicator === 'Z' ? 'Required' : ''}
                </td>
              </tr>))}
        </tbody>
      </table>
      {fullList && fullList.length > 5
        && <LgyLink to='/lgyhub/appraiser'>See more...</LgyLink>}
    </div>}

  const expiringLicenses = (expiringLicenses) =>
    <div className='mini-gear-expiring-licenses'>
      <span style={{ color: 'red' }}>The following license(s) will expire within 30 days:</span>
      <table className='usa-table-borderless'>
        <caption className='usa-sr-only'>Expiring Licenses</caption>
        <thead>
          <tr>
            <th scope='col'>License</th>
            <th scope='col'>Expiration Date</th>
            <th scope='col'>State</th>
          </tr>
        </thead>
        <tbody>
          {expiringLicenses.map(license =>
            <tr key={license.id}>
              <th scope='row'>{license.appraiserLicenseNumber}</th>
              <td>{license.appraiserLicenseExpirationDateString}</td>
              <td>{license.stateCode}</td>
            </tr>)}
        </tbody>
      </table>
      <span>For more information on the licenses, go to <LgyLink to='/lgyhub/appraiser/profile'>Personal / Business Profile</LgyLink></span>
    </div>

    if (fetching) {
      return <div className='appraisal-mini-gear fetching'>
        <h1>Appraiser Workspace</h1>
        <LgyLoadingIcon />
      </div>
    } else if (appraiserProfile.appraiserId) {
      return (inactiveAppraiser(appraiserProfile, user) ?
        <div className='appraisal-mini-gear inactive-message'>
          <h1>Appraiser Workspace</h1>
          {inactiveAppraiser(appraiserProfile, user)}
        </div>
        :
        <div className='appraisal-mini-gear'>
          <h1>Appraiser Workspace</h1>
          <div className='mini-gear-header'>
            <b>Name:</b> {appraiserProfile.firstName} {appraiserProfile.lastName}&nbsp;<b>|</b>&nbsp;
            <b>ID:</b> {appraiserProfile.appraiserVaId}&nbsp;<b>|</b>&nbsp;
            <b>Status:</b> {appraiserProfile.appraiserStatusCode} / {appraiserProfile.availabilityStatus}
          </div>
          {renderPendingAssignments(pendingAssignments, unacceptedAssignments)}
          {appraiserProfile.expiringLicenses.length > 0 && expiringLicenses(appraiserProfile.expiringLicenses)}
          <AppraisalSearchBar location='/lgyhub' />
        </div>
      )
    }

    return <span>There is a difference between the LGY HUB registration data and your existing appraiser record in SIM. 
      Please contact the VA RLC to have them correct mismatched data such as SSN, phone, etc. for you. 
      Once the RLC corrects your information, please log-in to LGY Hub again. 
      If you are not sure how to contact your VA RLC please see <a href='https://www.benefits.va.gov/HOMELOANS/contact_rlc_info.asp' 
      target='_blank' rel='noopener noreferrer'>https://www.benefits.va.gov/HOMELOANS/contact_rlc_info.asp</a>
    </span>
}

const mapStateToProps = (state) => ({
  fetching: (state.currentUser.fetching || state.appraiser.workspace.appraiserProfile.fetching || 
    state.appraiser.workspace.pendingAssignments.fetching || state.appraiser.workspace.unacceptedAssignments.fetching),
  appraiserProfile: state.appraiser.workspace.appraiserProfile,
  pendingAssignments: state.appraiser.workspace.pendingAssignments,
  unacceptedAssignments: state.appraiser.workspace.unacceptedAssignments,
  user: state.currentUser
})

const mapDispatchToProps = ({fetchAppraiser, fetchPendingAssignments, fetchUnacceptedAssignments })

export default connect(mapStateToProps, mapDispatchToProps)(AppraisalMiniGear)
